import type { Mol } from '@moleculeone/types';

import {
  ISynthesisCompoundNode,
  IRawTreeCompoundNode,
  ISynthesisReactionNode,
  IRawTreeReactionNode,
  ISynthesis,
  IRawTreeNodes,
} from '../types';

import { VirtualTree, VirtualTreeNode } from '../virtual/virtual';
import { InteractiveTree, InteractiveTreeNode, SynthesisTreeNodeType } from '../interactive-tree/InteractiveTree';

async function smilesToMap(smiles: string, smilesToMol: (SMILES: string) => Promise<Mol>): Promise<number[]> {
  const mol = await smilesToMol(smiles);

  return Object.entries(mol.atoms)
    .sort(([atom1Idx], [atom2Idx]) => {
      const a = Number(atom1Idx);
      const b = Number(atom2Idx);

      if (a <= b) {
        return -1;
      }

      return 1;
    })
    .map(([, atom]) => (atom as any).classNum as number);
}

function synthesisCompoundToTreeNode(node: ISynthesisCompoundNode): IRawTreeCompoundNode {
  const { id, type, smiles, productOf, substrateOf, emoleculesId } = node;

  return {
    id: Number(id),
    type,
    smiles,
    predecessor: substrateOf != null ? Number(substrateOf) : null,
    successors: productOf != null ? [Number(productOf)] : [],
    emoleculesId,
    best: true,
  };
}

async function synthesisReactionToTreeNode(
  node: ISynthesisReactionNode,
  smilesToMol: (SMILES: string) => Promise<Mol>
): Promise<IRawTreeReactionNode> {
  const { id, type, smiles, productId, substratesIds } = node;

  const [substrates, productSmiles] = smiles.split('>>');
  const substrateSmiles = substrates.split('.');

  const productToMap = await smilesToMap(productSmiles, smilesToMol);
  const substratesToMap = await Promise.all(substrateSmiles.map(smiles => smilesToMap(smiles, smilesToMol)));

  return {
    id: Number(id),
    type,
    predecessor: Number(productId),
    successors: substratesIds.map(Number),
    productToMap,
    substratesToMap,
    highlights: [],
    best: true,
  };
}

const isCompound = (dataElement: any) => Boolean(dataElement.smiles);

const createTreeNode = (nodeData: any) => {
  const { id } = nodeData;
  const node = new InteractiveTreeNode(
    id,
    isCompound(nodeData) ? SynthesisTreeNodeType.Compound : SynthesisTreeNodeType.Reaction,
    nodeData
  );
  return node;
};

// TODO: should we have a 'virtual/unknown' node type for virtual nodes, or should
// we assume they're reactions (currently this would be true)
const createVirtualNode = (id: any, type = SynthesisTreeNodeType.Reaction) => {
  const node = new VirtualTreeNode(id, type);
  return node;
};

const createNode = (parent: any, data: any, id: any, createVirtualNodes: any) => {
  const nodeData = data[String(id)];
  let node: undefined | InteractiveTreeNode<any, any>;
  if (nodeData) {
    node = createTreeNode(nodeData);
    nodeData.successors.forEach((nodeId: any) => {
      createNode(node, data, nodeId, createVirtualNodes);
    });
  } else if (createVirtualNodes) {
    node = createVirtualNode(id);
  }
  if (node) {
    parent.addChild(node);
  }
  return node;
};

const constructTree = <T, N extends VirtualTreeNode<T, N>>(
  data: IRawTreeNodes,
  rootNodeId = 0,
  createVirtualNodes: any
) => {
  const tree = new MTree();
  if (!data) return tree;
  const rootData = data[rootNodeId];
  const rootNode = createTreeNode(rootData);

  tree.setRootNode(rootNode as MTreeNode<T, N>);

  rootData.successors.forEach((nodeId: any) => {
    createNode(rootNode, data, nodeId, createVirtualNodes);
  });

  return tree;
};

// interface IMTree extends IVirtualTree<VirtualTree, VirtualTreeNode> {};
export class MTree<T, N extends VirtualTreeNode<T, N>> extends VirtualTree<T, N> {
  static parse(data: IRawTreeNodes, createVirtualNodes = true) {
    const smallestId = Math.min(...Object.values(data).map(({ id }) => Number(id)));
    const tree = constructTree(data, smallestId, createVirtualNodes);
    return tree;
  }

  static async parseSynthesis(
    data: ISynthesis,
    smilesToMol: (SMILES: string) => Promise<Mol>,
    createVirtualNodes = true
  ) {
    const { compounds, reactions } = data;

    const treeCompoundNodes = Object.values(compounds)
      .map(synthesisCompoundToTreeNode)
      .reduce((acc, node) => {
        acc[node.id] = node;
        return acc;
      }, {} as any);

    const treeReactions = await Promise.all(
      Object.values(reactions).map(reaction => synthesisReactionToTreeNode(reaction, smilesToMol))
    );
    const treeReactionNodes = treeReactions.reduce((acc, node) => {
      acc[node.id] = node;
      return acc;
    }, {} as any);

    return this.parse(
      {
        ...treeCompoundNodes,
        ...treeReactionNodes,
      },
      createVirtualNodes
    );
  }

  public serialize(): IRawTreeNodes {
    const nodes = Object.values(this.nodes).filter(node => !node.isVirtual);

    return nodes.reduce((acc, curr) => {
      return {
        ...acc,
        [curr.id]: curr.getData(),
      };
    }, {});
  }

  public static synthesisFromTree(tree: MTree<any, any>): ISynthesis {
    const nodes = tree.serialize();
    const compounds: Record<string, ISynthesisCompoundNode> = {};
    const reactions: Record<string, ISynthesisReactionNode> = {};
    let targetId = '';

    Object.values(nodes).forEach(node => {
      if (node.type === 'compound') {
        compounds[node.id] = {
          id: String(node.id),
          type: node.type,
          smiles: node.smiles,
          productOf: node.successors[0] ? String(node.successors[0]) : null,
          substrateOf: node.predecessor ? String(node.predecessor) : null,
          emoleculesId: node.emoleculesId,
          data: {},
        };
        if (compounds[node.id].productOf == null) {
          targetId = node.id;
        }
      } else {
        const { predecessor, successors } = node;
        const foundPredecessor = nodes[predecessor] as IRawTreeCompoundNode;
        const foundSuccessors = successors.map((successor: any) => nodes[successor] as IRawTreeCompoundNode);
        if (!foundPredecessor || foundSuccessors.some((successor: any) => !successor)) {
          return;
        }
        reactions[node.id] = {
          id: String(node.id),
          type: node.type,
          smiles: `${foundPredecessor.smiles}>>${foundSuccessors.map((successor: any) => successor.smiles).join('.')}`,
          productId: String(node.predecessor),
          substratesIds: node.successors.map(String),
        };
      }
    });

    return { targetId, compounds, reactions };
  }
}

export class MTreeNode<T, N extends VirtualTreeNode<T, N>> extends VirtualTreeNode<T, N> {}

export interface IMTreeNode<T, N extends InteractiveTreeNode<T, N>>
  extends InteractiveTreeNode<T, N>,
    VirtualTreeNode<T, N> {}

export interface IMTree<T, N extends MTreeNode<T, N>> extends InteractiveTree<T, N>, VirtualTree<T, N> {}

// export * from '../types';
